import { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { logger } from '../utils/logger';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Configure axios to send cookies with all requests
axios.defaults.withCredentials = true;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchUser = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/auth/me`);
      setUser(response.data);
      return response.data;
    } catch (error) {
      // Not authenticated or token expired - this is normal
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    // On mount, check if user is authenticated via cookie
    fetchUser();
  }, [fetchUser]);

  const login = useCallback(async (email, password) => {
    const response = await axios.post(`${API_URL}/auth/login`, { email, password });
    const { user: userData } = response.data;
    setUser(userData);
    // Re-fetch to get full user data including role
    await fetchUser();
    return userData;
  }, [fetchUser]);

  const register = useCallback(async (email, password, name) => {
    const response = await axios.post(`${API_URL}/auth/register`, { email, password, name });
    const { user: userData } = response.data;
    setUser(userData);
    return userData;
  }, []);

  // Google OAuth login - redirect to Emergent Auth
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const loginWithGoogle = useCallback(() => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }, []);

  // Process Google OAuth callback
  const processGoogleCallback = useCallback(async (sessionId) => {
    try {
      const response = await axios.post(`${API_URL}/auth/google/session`, { session_id: sessionId });
      const { user: userData } = response.data;
      setUser(userData);
      return userData;
    } catch (error) {
      logger.error('Google OAuth error:', error);
      throw error;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API_URL}/auth/logout`);
    } catch (error) {
      // Logout failed, but clear local state anyway
      logger.error('Logout error:', error);
    }
    setUser(null);
  }, []);

  const contextValue = useMemo(() => ({
    user,
    loading,
    login,
    register,
    logout,
    loginWithGoogle,
    processGoogleCallback,
    fetchUser,
    setUser,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin'
  }), [user, loading, login, register, logout, loginWithGoogle, processGoogleCallback, fetchUser]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
